import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RoutesPaths } from "../Router/constant";

const convergenceCardIcons = {
  "01": "/images/convergence-strategy-icon.png",
  "02": "/images/convergence-capital-icon.png",
  "03": "/images/convergence-scales-icon.png",
  "04": "/images/convergence-navigation-icon.svg",
};

const getConvergenceCardsPerView = () =>
  typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 4;

const formatDifferentiatorTitle = (title) => {
  const words = title.split(" ");
  const highlightedWord = words.pop();

  return (
    <>
      {words.join(" ")}
      {words.length > 0 && <br />}
      <span>{highlightedWord}</span>
    </>
  );
};

function OurPosition() {
  const { t } = useTranslation();
  const positioningSections = t("pages.ourPosition.positioning.sections", {
    returnObjects: true,
  });
  const convergenceCards = t("pages.ourPosition.convergence.cards", {
    returnObjects: true,
  });
  const differentiators = t("pages.ourPosition.differentiators.items", {
    returnObjects: true,
  });
  const stats = t("pages.ourPosition.stats", { returnObjects: true });

  const [convergenceCardsPerView, setConvergenceCardsPerView] = useState(
    getConvergenceCardsPerView
  );
  const [convergenceIndex, setConvergenceIndex] = useState(
    getConvergenceCardsPerView()
  );
  const [convergenceOffset, setConvergenceOffset] = useState(0);
  const [isConvergenceAnimating, setIsConvergenceAnimating] = useState(true);
  const [activeConvergenceControl, setActiveConvergenceControl] = useState("next");
  const convergenceTrackRef = useRef(null);

  const convergenceLoopPadding = Math.min(
    convergenceCardsPerView,
    convergenceCards.length
  );
  const extendedConvergenceCards = useMemo(
    () => [
      ...convergenceCards.slice(-convergenceLoopPadding),
      ...convergenceCards,
      ...convergenceCards.slice(0, convergenceLoopPadding),
    ],
    [convergenceCards, convergenceLoopPadding]
  );

  const handleConvergencePrev = () => {
    if (convergenceCards.length <= 1) {
      return;
    }

    setIsConvergenceAnimating(true);
    setConvergenceIndex((current) => current - 1);
    setActiveConvergenceControl("prev");
  };

  const handleConvergenceNext = () => {
    if (convergenceCards.length <= 1) {
      return;
    }

    setIsConvergenceAnimating(true);
    setConvergenceIndex((current) => current + 1);
    setActiveConvergenceControl("next");
  };

  useEffect(() => {
    const handleResize = () => {
      setConvergenceCardsPerView(getConvergenceCardsPerView());
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsConvergenceAnimating(false);
    setConvergenceIndex(convergenceLoopPadding);
  }, [convergenceCardsPerView, convergenceLoopPadding]);

  useEffect(() => {
    if (isConvergenceAnimating) {
      return undefined;
    }

    const resetAnimation = window.requestAnimationFrame(() => {
      setIsConvergenceAnimating(true);
    });

    return () => window.cancelAnimationFrame(resetAnimation);
  }, [isConvergenceAnimating]);

  useEffect(() => {
    const updateConvergenceOffset = () => {
      const track = convergenceTrackRef.current;
      if (!track) {
        return;
      }

      const firstCard = track.querySelector(".position-grid__card");
      if (!firstCard) {
        return;
      }

      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || 0);
      const cardWidth = firstCard.getBoundingClientRect().width;

      setConvergenceOffset(convergenceIndex * (cardWidth + gap));
    };

    updateConvergenceOffset();
    window.addEventListener("resize", updateConvergenceOffset);

    return () => window.removeEventListener("resize", updateConvergenceOffset);
  }, [convergenceCardsPerView, convergenceIndex, extendedConvergenceCards.length]);

  const handleConvergenceTransitionEnd = () => {
    if (convergenceIndex >= convergenceCards.length + convergenceLoopPadding) {
      setIsConvergenceAnimating(false);
      setConvergenceIndex(convergenceLoopPadding);
      return;
    }

    if (convergenceIndex < convergenceLoopPadding) {
      setIsConvergenceAnimating(false);
      setConvergenceIndex(convergenceCards.length + convergenceLoopPadding - 1);
    }
  };

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.delay;
            if (delay) {
              entry.target.style.transitionDelay = `${delay}ms`;
            }

            entry.target.classList.add("show");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="position-page">
      <section className="position-hero">
        <div className="main-container position-hero__content">
          <div className="row align-items-center">
            <div className="col-md-7">
              <div className="position-hero__copy">
                <h1 className="m-0 position-hero__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="100">
                  {t("pages.ourPosition.hero.title")}
                </h1>
                <p className="m-0 f-s-20 l-h-30 position-hero__text reveal-on-scroll fade-up" data-delay="200">
                  {t("pages.ourPosition.hero.text")}
                </p>
                <div className="position-hero__cta">
                  <NavLink
                    className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100"
                    to={RoutesPaths.CONTACT}
                  >
                    <span className="btn-text">{t("common.cta.exploreApproach")}</span>
                    <span className="arrow-circle">
                      <img src="/images/right-angle-arrow.png" alt="icon" />
                    </span>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="position-section position-section--positioning">
        <div className="main-container">
          <div className="position-heading">
            <h2 className="m-0 position-heading__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="100">
              {t("pages.ourPosition.positioning.title")}
            </h2>
            <p className="m-0 f-s-20 l-h-30 reveal-on-scroll fade-right" data-delay="150">
              {t("pages.ourPosition.positioning.text")}
            </p>
          </div>

          <div className="position-capabilities">
            {positioningSections.map((section, index) => (
              <div
                className={`position-layout-row${
                  index % 2 === 1 ? " position-layout-row--media-first" : ""
                }`}
                key={section.eyebrow}
              >
                <div className="position-copy">
                  <span className="position-copy__eyebrow">{section.eyebrow}</span>
                  <h3 className="m-0 position-copy__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="200">
                    {section.title}
                  </h3>
                  <p className="m-0 f-s-20 l-h-30 position-copy__text reveal-on-scroll fade-left" data-delay="300">
                    {section.description}
                  </p>

                  <div className="position-copy__capabilities reveal-on-scroll fade-left" data-delay="400">
                    <h4 className="m-0 mb-3 f-s-20 l-h-30">
                      {t("pages.ourPosition.positioning.keyCapabilities")}
                    </h4>
                    <ul className="position-copy__list">
                      {section.capabilities.map((item) => (
                        <li key={item}>
                          <img src="/images/ourposition-icon.svg" alt="" className="me-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="position-collage">
                  <div className="position-collage__large reveal-on-scroll fade-right" data-delay="200">
                    <img
                      src="/images/Strategy1.png"
                      alt="Chess pieces representing strategic thinking"
                    />
                  </div>
                  <div className="position-collage__small reveal-on-scroll fade-left" data-delay="200">
                    <img src="/images/Strategy2.png" alt="Chess board detail" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="position-section position-section--convergence">
        <div className="main-container">
          <div className="position-convergence__header">
            <div className="position-convergence__copy">
              <h2 className="m-0 position-heading__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="100">
                {t("pages.ourPosition.convergence.title")}
              </h2>
              <p className="m-0 f-s-20 l-h-30 reveal-on-scroll fade-right" data-delay="150">
                {t("pages.ourPosition.convergence.text")}
              </p>
            </div>

            <div className="position-convergence__controls reveal-on-scroll fade-right" data-delay="200">
              <button
                className={`position-convergence__control${
                  activeConvergenceControl === "prev" ? " is-active" : ""
                }`}
                type="button"
                onClick={handleConvergencePrev}
                aria-label={t("common.actions.showPrevious")}
              >
                <span>&larr;</span>
              </button>
              <button
                className={`position-convergence__control${
                  activeConvergenceControl === "next" ? " is-active" : ""
                }`}
                type="button"
                onClick={handleConvergenceNext}
                aria-label={t("common.actions.showNext")}
              >
                <span>&rarr;</span>
              </button>
            </div>
          </div>

          <div className="position-grid reveal-on-scroll fade-up" data-delay="250">
            <div
              className="position-grid__track"
              ref={convergenceTrackRef}
              onTransitionEnd={handleConvergenceTransitionEnd}
              style={{
                transform: `translateX(-${convergenceOffset}px)`,
                transition: isConvergenceAnimating
                  ? "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)"
                  : "none",
              }}
            >
              {extendedConvergenceCards.map((card, index) => (
                <article
                  className="position-grid__card"
                  key={`${card.id}-${index}`}
                  aria-hidden={
                    index < convergenceIndex ||
                    index >= convergenceIndex + convergenceCardsPerView
                  }
                >
                  <div className="position-grid__top">
                    <div className="position-grid__icon">
                      <img src={convergenceCardIcons[card.id]} alt="" width="30" height="30" />
                    </div>
                    <span className="position-grid__id">{card.id}</span>
                  </div>
                  <h3 className="m-0 position-grid__title f-s-20 l-h-30">{card.title}</h3>
                  <p className="m-0 f-s-16 l-h-24">{card.description}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="position-tech-strip reveal-on-scroll fade-up" data-delay="300">
            <img src="/images/Technology-svg.svg" alt="" />
            <span>{t("pages.ourPosition.convergence.techStrip")}</span>
          </div>
        </div>
      </section>

      <section className="position-section position-section--differentiators-heading">
        <div className="main-container">
          <div className="position-heading competitive-bottom">
            <h2 className="m-0 position-heading__title f-s-48 l-h-60 reveal-on-scroll fade-left" data-delay="100">
              {t("pages.ourPosition.differentiators.title")}
            </h2>
            <p className="m-0 f-s-20 l-h-30 reveal-on-scroll fade-right" data-delay="150">
              {t("pages.ourPosition.differentiators.text")}
            </p>
          </div>
        </div>
      </section>

      {differentiators.map((item, index) => (
        <section
          className={`position-differentiator-section${
            index === 0 ? " position-differentiator-section--first" : ""
          }`}
          key={item.id}
        >
          <div className="main-container">
            <article className="position-differentiators__row reveal-on-scroll fade-up" tabIndex={0} data-delay="200">
              <span className="position-differentiators__id">{item.id}</span>
              <h3 className="m-0 position-differentiators__title f-s-36 l-h-48">
                {formatDifferentiatorTitle(item.title)}
              </h3>
              <p className="m-0 f-s-16 l-h-30 position-differentiators__text">
                <span className="position-differentiators__text-inner">{item.description}</span>
              </p>
              <span className="position-differentiators__badge">{item.badge}</span>
            </article>
          </div>
        </section>
      ))}

      <section className="position-section position-section--stats">
        <div className="main-container">
          <div className="position-stats">
            {stats.map((item) => (
              <div className="position-stats__item" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-stratagic">
        <div className="main-container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-9 text-center px-3">
              <h2 className="m-0 f-w-600 f-s-48 l-h-60 c-s about-cta__title reveal-on-scroll fade-left" data-delay="100">
                {t("common.siteCta.title")}
              </h2>
              <p className="m-0 f-s-20 l-h-30 c-s about-cta__text reveal-on-scroll fade-right" data-delay="150">
                {t("common.siteCta.text")}
              </p>

              <NavLink
                className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100 mx-auto"
                to={RoutesPaths.CONTACT}
              >
                <span className="btn-text">{t("common.cta.contact")}</span>
                <span className="arrow-circle">
                  <img src="/images/right-angle-arrow.png" alt="icon" />
                </span>
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default OurPosition;
